import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Newspaper, MapPin, CalendarDays, ArrowLeft, ArrowRight, Store } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import KeywordsSuggestBlock, { buildKeywords } from "@/components/KeywordsSuggestBlock";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import FbGroupShare from "@/components/FbGroupShare";
import FeaturedPartners from "@/components/FeaturedPartners";
import { buildFbPost } from "@/lib/fbPost";

function esc(s) { return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
function inlineMd(s) {
  let t = esc(s);
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  return t.replace(/\n/g, "<br/>");
}
function renderContent(content) {
  const blocks = (content || "").split(/\n{2,}/).filter(Boolean);
  return blocks.map((b, i) => {
    const img = b.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img) return (
      <figure key={i} className="my-7" data-testid={`blog-body-image-${i}`}>
        <img src={img[2]} alt={img[1]} loading="lazy" className="w-full rounded-2xl border border-slate-100 object-cover" />
        {img[1] && <figcaption className="mt-2 text-center text-xs text-slate-400">{img[1]}</figcaption>}
      </figure>
    );
    if (b.startsWith("## ")) return <h2 key={i} className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-3">{b.slice(3).replace(/\*\*/g, "")}</h2>;
    if (b.startsWith("# ")) return <h2 key={i} className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-3">{b.slice(2).replace(/\*\*/g, "")}</h2>;
    return <p key={i} className="mb-4" dangerouslySetInnerHTML={{ __html: inlineMd(b) }} />;
  });
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: post ? `${post.title} | PROXIreduc-28` : "Article | PROXIreduc-28",
    description: post ? (post.meta_description || post.excerpt || post.title) : "",
    canonicalPath: `/blog/${slug}`,
    image: (post && post.cover_image) ? post.cover_image : "/og-blog.jpg",
    keywords: post ? buildKeywords(post.category, post.city).join(", ") : undefined,
    type: "article",
  });

  useEffect(() => {
    if (!post) return;
    const ld = {
      "@context": "https://schema.org", "@type": "Article",
      headline: post.title, description: post.meta_description || post.excerpt || "",
      image: post.cover_image || undefined, datePublished: post.published_at || undefined,
      author: { "@type": "Organization", name: "PROXIreduc-28" },
      publisher: { "@type": "Organization", name: "PROXIreduc-28" },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(ld);
    el.setAttribute("data-blog-ld", "1");
    document.head.appendChild(el);
    return () => { document.querySelectorAll('script[data-blog-ld]').forEach((n) => n.remove()); };
  }, [post]);

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="blog-article-404">
      <h1 className="font-heading text-2xl font-bold">Article introuvable</h1>
      <Link to="/blog" className="mt-4 inline-flex items-center gap-1 text-[#E05638] font-semibold"><ArrowLeft size={16} /> Retour au blog</Link>
    </div>
  );
  if (!post) return <div className="max-w-2xl mx-auto px-4 py-20 text-slate-500">Chargement…</div>;

  const track = (ch) => { api.post(`/blog/${slug}/share`, { channel: ch }).catch(() => {}); };
  const fbText = buildFbPost({ title: post.title, excerpt: post.excerpt, city: post.city, category: post.category });
  const vitrineUrl = `${process.env.REACT_APP_BACKEND_URL}/api/blog-vitrine/${slug}`;

  return (
    <article className="pb-24 sm:pb-16" data-testid="blog-article">
      {post.cover_image && (
        <div className="w-full aspect-[2.4/1] max-h-[420px] bg-slate-100 overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" className="mt-6 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E05638]"><ArrowLeft size={14} /> Tous les articles</Link>
        <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
          {post.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {post.city}</span>}
          {post.category && <span className="px-2 py-0.5 rounded-full bg-orange-50 text-[#E05638] font-semibold">{post.category}</span>}
          {post.published_at && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(post.published_at).toLocaleDateString("fr-FR")}</span>}
        </div>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="blog-article-title">{post.title}</h1>
        {post.author_name && <p className="mt-2 text-sm text-slate-400 italic" data-testid="blog-article-author">Par {post.author_name}</p>}
        {post.excerpt && <p className="mt-3 text-lg text-slate-600">{post.excerpt}</p>}

        <div className="mt-5 flex items-center gap-2 flex-wrap border-y border-slate-100 py-3" data-testid="blog-share">
          <span className="text-sm font-semibold text-slate-500 mr-1">Partager :</span>
          <ShareButton url={vitrineUrl} title={post.title} text={fbText} testId="blog-share" onShare={track} />
        </div>

        <div className="mt-8 prose-proxi">{renderContent(post.content)}</div>
        <KeywordsSuggestBlock category={post.category} city={post.city} />
        <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title }]} />

        {post.partners?.length > 0 && (
          <div className="mt-10" data-testid="blog-partners">
            <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <Store size={18} className="text-[#E05638]" /> Nos partenaires {post.city ? `à ${post.city}` : "près de chez vous"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">Des professionnels locaux à découvrir sur PROXIreduc-28.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {post.partners.map((pa) => (
                <Link key={pa.id} to={`/commerce/${pa.id}`} data-testid={`blog-partner-${pa.id}`}
                  className="flex items-center justify-between gap-2 p-4 rounded-xl border border-slate-200 bg-white hover:border-[#E05638] transition-colors">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{pa.name}</div>
                    {pa.city && <div className="text-xs text-slate-500 inline-flex items-center gap-1"><MapPin size={11} /> {pa.city}</div>}
                  </div>
                  <ArrowRight size={16} className="text-[#E05638] shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-orange-50 to-white border border-orange-200 text-center">
          <div className="font-heading text-xl font-bold text-slate-900">Profitez des bons plans près de chez vous</div>
          <p className="mt-1 text-sm text-slate-600">Rejoignez gratuitement le réseau des bons plans d'Eure-et-Loir.</p>
          <Link to="/inscription" className="mt-4 inline-flex items-center gap-1 btn-shine text-white font-semibold px-5 py-2.5 rounded-xl" data-testid="blog-cta-signup">
            Créer mon compte gratuit <ArrowRight size={16} />
          </Link>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-100" data-testid="blog-share-bottom-section">
          <div className="flex items-center gap-2 flex-wrap" data-testid="blog-share-bottom">
            <span className="text-xs font-semibold text-slate-400 mr-1">Partager :</span>
            <ShareButton url={vitrineUrl} title={post.title} text={fbText} testId="blog-share-bottom-btn" onShare={track} />
          </div>
          <FbGroupShare
            small
            showTopCopy
            testId="fb-group-share-bottom"
            text={fbText}
            shareUrl={vitrineUrl}
            cityHint={post.city || ""}
            onShared={() => track("fb_groups")}
          />
        </div>

        {post.related?.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><Newspaper size={18} className="text-[#E05638]" /> À lire aussi</h2>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              {post.related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="block p-4 rounded-xl border border-slate-200 bg-white hover:border-[#E05638] transition-colors">
                  <div className="font-semibold text-slate-900 text-sm line-clamp-2">{r.title}</div>
                  {r.city && <div className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1"><MapPin size={11} /> {r.city}</div>}
                </Link>
              ))}
            </div>
          </div>
        )}

        <FeaturedPartners testId="blog-featured-partners" track channel="blog" city={post.city || ""} />
      </div>
    </article>
  );
}
